import { useState, useEffect } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faEdit, faSpinner } from '@/fontawesome.ts';
import Modal from '@/components/ui/Modal.tsx';
import { APIRoutes } from '@/services/api.routes.ts';
import { handleAPIError, showSuccessToast, showErrorToast } from '@/utils/toasts.ts';
import { toCurrencyCentValue } from '@/utils/helpers.ts';
import type { RecommendationSwingtrade, SwingTradeAdjustement } from '@/models';

interface EditSwingTradeAdjustementModalProps {
  isOpen: boolean;
  onClose: () => void;
  swingTrade: RecommendationSwingtrade | null;
  adjustment: SwingTradeAdjustement | null;
  onAdjustmentUpdated: (swingTrade: RecommendationSwingtrade) => void;
}

export default function EditSwingTradeAdjustementModal({
  isOpen,
  onClose,
  swingTrade,
  adjustment,
  onAdjustmentUpdated
}: EditSwingTradeAdjustementModalProps) {
  const [delta, setDelta] = useState<number>(0);
  const [price, setPrice] = useState<number>(0);
  const [timestamp, setTimestamp] = useState<string>('');
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (adjustment) {
      setDelta(adjustment.delta * 100); // Convert to percentage
      setPrice(adjustment.price / 100); // Convert from cents

      // Convert RFC3339 timestamp to datetime-local format (yyyy-MM-ddTHH:mm)
      const date = new Date(adjustment.timestamp);
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const day = String(date.getDate()).padStart(2, '0');
      const hours = String(date.getHours()).padStart(2, '0');
      const minutes = String(date.getMinutes()).padStart(2, '0');
      setTimestamp(`${year}-${month}-${day}T${hours}:${minutes}`);
    }
  }, [adjustment]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!swingTrade || !adjustment) return;

    // Validation
    if (delta === 0) {
      showErrorToast('% des Depots darf nicht 0 sein');
      return;
    }

    if (price <= 0) {
      showErrorToast('Preis muss größer als 0 sein');
      return;
    }

    if (!timestamp) {
      showErrorToast('Zeitstempel ist erforderlich');
      return;
    }

    setLoading(true);

    try {
      const requestData: { delta?: number; price?: number; timestamp?: string } = {};

      // Only send changed fields
      const originalDelta = adjustment.delta * 100;
      const originalPrice = adjustment.price / 100;

      if (delta !== originalDelta) {
        requestData.delta = delta / 100; // Convert to 0-1 range
      }

      if (price !== originalPrice) {
        requestData.price = toCurrencyCentValue(price);
      }

      // Always send timestamp if changed (convert to RFC3339)
      const originalTimestamp = new Date(adjustment.timestamp);
      const newTimestamp = new Date(timestamp);
      if (newTimestamp.getTime() !== originalTimestamp.getTime()) {
        requestData.timestamp = newTimestamp.toISOString();
      }

      // Only send if there are changes
      if (Object.keys(requestData).length === 0) {
        showErrorToast('Keine Änderungen vorgenommen');
        onClose();
        return;
      }

      const updatedSwingTrade = await APIRoutes.updateSwingTradeAdjustement(
        swingTrade.id,
        adjustment.id,
        requestData
      );
      onAdjustmentUpdated(updatedSwingTrade);
      showSuccessToast('Einstieg erfolgreich aktualisiert');
      onClose();
    } catch (error) {
      handleAPIError(error, 'Fehler beim Aktualisieren des Einstiegs');
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    if (!loading) {
      onClose();
    }
  };

  if (!swingTrade || !adjustment) return null;

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      title="Einstieg bearbeiten"
      icon={<FontAwesomeIcon icon={faEdit} className="w-5 h-5 text-blue-600" />}
      maxWidth="md"
      closeDisabled={loading}
    >
      <form onSubmit={handleSubmit} className="p-6 space-y-6">
        {/* Delta */}
        <div>
          <label htmlFor="delta" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
            % des Depots *
          </label>
          <input
            type="number"
            id="delta"
            value={delta || ''}
            onChange={(e) => setDelta(parseFloat(e.target.value) || 0)}
            step="0.01"
            className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-blue-500 focus:border-blue-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
            placeholder="z.B. 25.00 oder -15.00"
            disabled={loading}
            autoFocus
          />
          <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
            Positiv = Kaufen, Negativ = Verkaufen
          </p>
        </div>

        {/* Price */}
        <div>
          <label htmlFor="price" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
            Preis  *
          </label>
          <input
            type="number"
            id="price"
            value={price || ''}
            onChange={(e) => setPrice(parseFloat(e.target.value) || 0)}
            step="0.01"
            min="0"
            className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-blue-500 focus:border-blue-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
            placeholder="100.00"
            disabled={loading}
          />
        </div>

        {/* Timestamp */}
        <div>
          <label htmlFor="timestamp" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
            Zeitstempel *
          </label>
          <input
            type="datetime-local"
            id="timestamp"
            value={timestamp}
            onChange={(e) => setTimestamp(e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-blue-500 focus:border-blue-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
            disabled={loading}
          />
          <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
            Der Zeitpunkt, zu dem der Einstieg vorgenommen wurde
          </p>
        </div>

        <div className="flex justify-end space-x-3 pt-4 border-t border-gray-200 dark:border-gray-700">
          <button
            type="button"
            onClick={handleClose}
            disabled={loading}
            className="px-4 py-2 text-gray-700 dark:text-gray-300 bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 rounded-lg transition-colors cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Abbrechen
          </button>
          <button
            type="submit"
            disabled={loading || delta === 0 || price <= 0 || !timestamp}
            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors font-medium flex items-center gap-2 cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {loading ? (
              <>
                <FontAwesomeIcon icon={faSpinner} className="w-4 h-4 animate-spin" />
                Speichern...
              </>
            ) : (
              'Speichern'
            )}
          </button>
        </div>
      </form>
    </Modal>
  );
}
